import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// `path` is the chain of selected segment keys from each enclosing
// DeepLinkTabs. useDeepLinkParam prefixes this onto param names so
// filters are auto-scoped per pane — `filter` inside the Salt tab
// becomes `salt.filter` in the URL; the same hook call inside Combos
// becomes `combos.filter`. No cross-pane collisions, no manual cleanup.
const DeepLinkContext = createContext({ depth: 0, defaults: [], path: [] });

export const DeepLinkContextProvider = DeepLinkContext.Provider;

export function useDeepLinkContext() {
    return useContext(DeepLinkContext);
}

function safeDecode(s) {
    try {
        return decodeURIComponent(s);
    } catch {
        return s;
    }
}

// Hash format: `#segment/segment?key=val&key=val`
export function parseHash(hash) {
    const raw = hash.replace(/^#/, '');
    const qIndex = raw.indexOf('?');
    const pathPart = qIndex === -1 ? raw : raw.slice(0, qIndex);
    const queryPart = qIndex === -1 ? '' : raw.slice(qIndex + 1);

    const segments = pathPart.split('/').filter(Boolean).map(safeDecode);

    const params = {};
    if (queryPart) {
        for (const pair of queryPart.split('&')) {
            if (!pair) continue;
            const eq = pair.indexOf('=');
            const k = safeDecode(eq === -1 ? pair : pair.slice(0, eq));
            const v = eq === -1 ? '' : safeDecode(pair.slice(eq + 1));
            if (k) params[k] = v;
        }
    }

    return { segments, params };
}

function writeHash({ segments, params }) {
    const path = segments.map(encodeURIComponent).join('/');
    const keys = Object.keys(params).sort();
    const query = keys
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join('&');
    const body = query ? `${path}?${query}` : path;
    const hash = body ? `#${body}` : '';
    const url = window.location.pathname + window.location.search + hash;
    window.history.replaceState(null, '', url);
    window.dispatchEvent(new Event('hashchange'));
}

function useHashState() {
    const [state, setState] = useState(() => parseHash(window.location.hash));
    useEffect(() => {
        const handler = () => setState(parseHash(window.location.hash));
        window.addEventListener('hashchange', handler);
        return () => window.removeEventListener('hashchange', handler);
    }, []);
    return state;
}

export function useDeepLinkSegment(defaultKey) {
    const { depth, defaults, path = [] } = useDeepLinkContext();
    const { segments } = useHashState();

    const currentKey = segments[depth] ?? defaultKey;

    const setKey = useCallback(
        (newKey) => {
            const { segments: current, params } = parseHash(window.location.hash);
            const nextSegments = current.slice(0, depth);
            // Ancestor tabs on their default key are absent from the hash. Pad
            // their positions with the registered defaults so a deeper write
            // doesn't end up occupying a shallower slot and clobbering a
            // parent tab's selection (which caused a blank panel on mobile
            // when both outer + inner DeepLinkTabs were present).
            while (nextSegments.length < depth) {
                nextSegments.push(String(defaults[nextSegments.length] ?? ''));
            }
            if (newKey != null && newKey !== defaultKey) {
                nextSegments.push(String(newKey));
            }

            // Drop every param scoped under the leaving segment. The scope
            // prefix is `<enclosingPath>.<oldKey>`, so any param name equal
            // to that or starting with `<prefix>.` belongs to the subtree
            // we're navigating away from and should be cleared.
            let nextParams = params;
            if (currentKey !== newKey) {
                const oldPrefix = [...path, currentKey].filter(Boolean).join('.');
                if (oldPrefix) {
                    nextParams = {};
                    for (const [k, v] of Object.entries(params)) {
                        if (k === oldPrefix || k.startsWith(`${oldPrefix}.`)) continue;
                        nextParams[k] = v;
                    }
                }
            }
            writeHash({ segments: nextSegments, params: nextParams });
        },
        [depth, defaults, defaultKey, currentKey, path]
    );

    return [currentKey, setKey];
}

// Serializes a CategoryChipBrowser `{ kind, key }` selection into a single
// hash param so chip filters survive reload / share. Uses `.` as separator
// — encodeURIComponent leaves it alone, so URLs stay readable.
const SELECTION_SEP = '.';
const SELECTION_KINDS = new Set(['chip', 'group']);

function serializeSelection(sel) {
    if (!sel || !sel.kind || !sel.key) return '';
    if (!SELECTION_KINDS.has(sel.kind)) return '';
    return `${sel.kind}${SELECTION_SEP}${sel.key}`;
}

function parseSelection(raw) {
    if (!raw) return null;
    const idx = raw.indexOf(SELECTION_SEP);
    if (idx <= 0) return null;
    const kind = raw.slice(0, idx);
    const key = raw.slice(idx + 1);
    if (!SELECTION_KINDS.has(kind) || !key) return null;
    return { kind, key };
}

export function useDeepLinkSelection(paramName) {
    const [raw, setRaw] = useDeepLinkParam(paramName, '');
    const selection = parseSelection(raw);
    const setSelection = useCallback((next) => setRaw(serializeSelection(next)), [setRaw]);
    return [selection, setSelection];
}

// By default, params are scoped by the enclosing DeepLinkTabs path so a
// `filter` inside the Salt pane reads/writes `salt.filter`. Pass
// `{ scope: false }` to opt out (e.g. a page-wide `bin` that should work
// identically across mobile/desktop layouts, where the wrapping tabs
// structure differs).
export function useDeepLinkParam(name, defaultValue, { scope = true } = {}) {
    const { path = [] } = useDeepLinkContext();
    const scopedName = useMemo(() => {
        if (!scope) return name;
        const parts = path.filter(Boolean);
        return parts.length > 0 ? `${parts.join('.')}.${name}` : name;
    }, [path, name, scope]);

    const { params } = useHashState();

    const raw = params[scopedName];
    const currentValue = raw === undefined ? defaultValue : raw;

    const setValue = useCallback(
        (newValue) => {
            const { segments, params: current } = parseHash(window.location.hash);
            const nextParams = { ...current };
            const isDefault =
                newValue == null ||
                newValue === defaultValue ||
                String(newValue) === String(defaultValue);
            if (isDefault) {
                delete nextParams[scopedName];
            } else {
                nextParams[scopedName] = String(newValue);
            }
            writeHash({ segments, params: nextParams });
        },
        [scopedName, defaultValue]
    );

    return [currentValue, setValue];
}
